import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './store';
import App from './App.tsx';
import { ThemeProvider } from './components/ThemeProvider';
import './index.css';
import './styles/dnl-transitions.css';

// T-26: PWA Service Worker Registration
import { registerServiceWorker, unregisterServiceWorker } from './lib/serviceWorker';

// T-26: Global install prompt storage
declare global {
  interface Window {
    deferredPrompt: any;
  }
}

// T-26: Prevent multiple registrations
let pwaInitialized = false;

// In development, aggressively remove any existing Service Worker before loading the app
// This must run before React renders to avoid SW interfering with Vite HMR
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  (async () => {
    try {
      const alreadyCleaned = sessionStorage.getItem('sw-dev-cleaned');
      if (!alreadyCleaned) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister().catch(() => {})));
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(n => caches.delete(n).catch(() => {})));
        sessionStorage.setItem('sw-dev-cleaned', '1');
        // Reload once to ensure the page is no longer controlled by a SW
        location.reload();
      }
    } catch {}
  })();
}

// T-26: Capture install prompt globally (before any components mount)
window.addEventListener('beforeinstallprompt', (e) => {
  console.log('[PWA] Global install prompt captured:', e);
  e.preventDefault();
  window.deferredPrompt = e;
  
  // Dispatch custom event for components to listen to
  window.dispatchEvent(new CustomEvent('pwa-install-available', { detail: e }));
});

window.addEventListener('appinstalled', () => {
  console.log('[PWA] App installed successfully');
  window.deferredPrompt = null;
  window.dispatchEvent(new CustomEvent('pwa-app-installed'));
});

// T-26: Initialize PWA functionality only once
const initializePWA = async () => {
  // Prevent multiple initializations
  if (pwaInitialized) {
    console.log('[PWA] Already initialized, skipping...');
    return;
  }
  
  pwaInitialized = true;
  
  try {
    console.log('[PWA] Initializing Progressive Web App features...');
    
    // Register service worker for offline functionality
    await registerServiceWorker({
      onSuccess: (registration) => {
        console.log('[PWA] Service Worker registered successfully:', registration);
      },
      onUpdate: (registration) => {
        console.log('[PWA] New service worker version available');
        
        // You could show a notification to the user here
        if (window.confirm('A new version of the app is available. Reload to update?')) {
          registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
          window.location.reload();
        }
      },
      onError: (error) => {
        console.warn('[PWA] Service Worker registration failed:', error);
        // App will continue to work without PWA features
      },
      onOffline: () => {
        console.log('[PWA] App is now offline');
        // Could show offline notification
      },
      onOnline: () => {
        console.log('[PWA] App is back online');
        // Could hide offline notification
      }
    });

    // T-26: Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('[PWA] App is running in standalone mode (installed)');
    }

    console.log('[PWA] PWA initialization completed');

  } catch (error) {
    console.warn('[PWA] PWA initialization failed:', error);
    // App continues to work as a regular web app
  }
};

// Start PWA initialization only in production; actively unregister in dev to avoid SW interfering with Vite HMR
if (import.meta.env.PROD) {
  initializePWA();
} else {
  unregisterServiceWorker().catch(() => {
    // Ignore errors during dev SW unregister
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </Provider>
  </React.StrictMode>,
);